import React, { useMemo } from 'react';
import { FloorContainer } from "./AreaAndDeskUsageView";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectActiveDay } from "../../../../store/editMovements.duck";
import { selectedRootOffice } from "../../../../store/officeView.duck";
import { BodyRegular, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { WithCarParkingBookings } from '@features/whereabouts/hooks/WithCarParkingBookings';
import { CarParkBooking } from "@features/desks/services/OfficeCapacityService";
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { WithOfficeCapacity } from '@features/desks/hooks/WithOfficeCapacity';
import { CarParkPlanBooking } from '@features/whereabouts/services/CarParkingPlanService';
import { CarParkBookingRow } from "./CarParkBookingRow";
import { naturalSort } from "@shared/utils/ArrayUtils";

export function OfficeCarParkingList(props: Props) {
  const {t} = useTranslation();
  const activeDate = useSelector(selectActiveDay);
  const rootOffice = useSelector(selectedRootOffice);
  const capacity = WithOfficeCapacity(rootOffice, activeDate);
  const carParkUsage = WithCarParkingBookings(rootOffice, activeDate);

  const carParkingSpaces = useMemo(() => {
    return capacity?.capacity?.carParkingCapacity ?? 0
  }, [capacity]);

  const usedCarParkingSpaces = useMemo(() => {
    if (carParkUsage) {
      if (carParkUsage.carParkPlanBookings && carParkUsage.carParkPlanBookings.length > 0) {
        return carParkUsage.carParkPlanBookings.length
      }
      if (carParkUsage.bookings && carParkUsage.bookings.length) {
        return carParkUsage.bookings.length;
      }
    }
    return 0;
  }, [carParkUsage]);

  const orderedParkingSpaceBookings: CarParkPlanBooking[] = useMemo(() => {
    const sideOfDaySorted = naturalSort(carParkUsage.carParkPlanBookings, 'sideOfDay')
    return naturalSort(sideOfDaySorted, 'label');
  }, [carParkUsage.carParkPlanBookings])

  return (
    <FloorContainer>
      <HeadlineSmall style={{marginBottom: 3}}>{t('whereabouts.car-park-bookings')}</HeadlineSmall>
      <BodyRegular>{t('whereabouts.car-park-capacity-and-usage', {spaces: carParkingSpaces, bookings:  usedCarParkingSpaces})}</BodyRegular>
      {orderedParkingSpaceBookings.map((booking: CarParkPlanBooking, key: number) => (
        <Row key={key}>
          <UserAvatarAndName firstName={booking.firstName}
                             lastName={booking.lastName}
                             colour={'darkBlue'}
                             hideName={true} />
          <Column>
            <BodyRegular>{`${booking.firstName} ${booking.lastName}`}</BodyRegular>
            <BodyVerySmall style={{marginBottom: 3}}>{`${booking.label}`}</BodyVerySmall>
            {!!booking.carReg ? <BodyVerySmall>{booking.carReg}</BodyVerySmall> : <BodyVerySmall>No registered car</BodyVerySmall>}
          </Column>

          <BodyRegular className={"period"} colour={Colours.blue}>{booking.sideOfDay}</BodyRegular>
        </Row>
      ))}
      {carParkUsage.bookings.map((booking: CarParkBooking, key: number) => (<React.Fragment key={key}>
        <CarParkBookingRow booking={booking} />
      </React.Fragment>))}
    </FloorContainer>
  )
}

const Row = styled.div`
  display: flex;
  align-content: center;
  border-top: 1px solid ${Colours.lightGrey};
  margin-top: 20px;
  padding-top: 20px;
  .period {
    align-self: center;
    margin-left: auto;
  }
  .userAvatarAndName__avatar {
    align-self: center;
    margin-right: 16px;
  }
  &:last-child {
    margin-bottom: 20px;
  }
`

interface Props {
}
